/**
 * PedidosDropdown — Bandeja de pedidos nuevos de clientes, al lado izquierdo
 * de las @-menciones.
 *
 * v8.6.24 · Fase 58 (decisión de producto) — antes los pedidos nuevos solo
 * notificaban mezclados en la campana general (junto con asignaciones,
 * calendario, facturas, QC, etc.) — con el volumen de materiales diario de
 * una EC, un pedido nuevo se perdía fácil ahí. Mismo patrón exacto que
 * MentionsDropdown.jsx, filtrado a tipo:"pedido-cliente" en vez de
 * mention/mencion-chat.
 *
 * Click en item → navega a /pedidos-cliente (o al link guardado en la notif).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox as InboxIcon, CheckCheck } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { Tooltip } from "./ui/tooltip.jsx";
import { BandejaPanel, PildoraBandeja, FilaBandeja, TituloGrupo, EsqueletoBandeja, VacioBandeja, agruparPorDia, haceCuanto } from "./BandejaPanel.jsx";
import { useAuth } from "../context/AuthContext.jsx";

const POLL_INTERVAL = 20_000;
const PEDIDO_TIPOS = "pedido-cliente";

export function PedidosDropdown() {
  const { miembro } = useAuth();
  const userId = miembro?.id;
  const navigate = useNavigate();
  const [open, setOpen]               = useState(false);
  const [tab, setTab]                 = useState("unread"); // unread | all
  const [pedidos, setPedidos]         = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading]         = useState(false);
  const wrapRef   = useRef(null);
  const btnRef    = useRef(null);
  const panelRef  = useRef(null);

  const fetchPedidos = useCallback(async (currentTab) => {
    if (!userId) { setPedidos([]); setUnreadCount(0); return; }
    setLoading(true);
    try {
      const params = { user_id: userId, tipos: PEDIDO_TIPOS, limit: 30 };
      if ((currentTab || tab) === "unread") params.unread_only = "true";
      const res = await api.getNotifications(params);
      setPedidos(res.notifications ?? []);
      if (typeof res.unread_count === "number") setUnreadCount(res.unread_count);
    } catch {
      setPedidos([]);
    } finally {
      setLoading(false);
    }
  }, [userId, tab]);

  // Poll + refresh on focus/visibility
  useEffect(() => {
    fetchPedidos();
    const id = setInterval(() => {
      if (!document.hidden) fetchPedidos();
    }, POLL_INTERVAL);
    const onFocus = () => fetchPedidos();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchPedidos]);

  // Refetch al cambiar tab
  useEffect(() => {
    if (open) fetchPedidos(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Click outside cierra
  useEffect(() => {
    function onClick(e) {
      const inBtn = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inBtn && !inPanel) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  async function handleClickPedido(p) {
    if (!p.leida) {
      api.markNotifRead(p.id).catch(() => {});
      setPedidos(prev => prev.map(n => n.id === p.id ? { ...n, leida: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    navigate(p.link || "/pedidos-cliente");
    setOpen(false);
  }

  async function handleMarkAllRead() {
    if (!userId || unreadCount === 0) return;
    setPedidos(prev => prev.map(n => ({ ...n, leida: true })));
    setUnreadCount(0);
    await Promise.all(
      pedidos.filter(p => !p.leida).map(p => api.markNotifRead(p.id).catch(() => {}))
    );
    if (tab === "unread") setPedidos([]);
  }

  const grupos = agruparPorDia(pedidos);
  return (
    <div ref={wrapRef} className="relative">
      <Tooltip content="Pedidos nuevos" side="bottom">
        <button
          ref={btnRef}
          onClick={() => setOpen(o => !o)}
          className="relative h-[46px] w-[46px] inline-flex items-center justify-center rounded-full bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:shadow-md text-muted-foreground hover:text-foreground transition-all"
          aria-label="Bandeja de pedidos nuevos"
        >
          <InboxIcon className="h-[22px] w-[22px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#0E7490] text-white text-[12px] font-extrabold flex items-center justify-center ring-2 ring-background">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      <BandejaPanel
        abierto={open} onCerrar={() => setOpen(false)} anclaRef={btnRef} panelRef={panelRef}
        titulo="Pedidos nuevos" subtitulo={unreadCount > 0 ? `${unreadCount} sin leer` : "Todo al día"}
        acciones={tab === "unread" && unreadCount > 0 ? (
          <button onClick={handleMarkAllRead} className="h-10 px-3 rounded-full text-[14px] font-extrabold text-[#0E7490] flex items-center gap-1 active:bg-muted">
            <CheckCheck className="h-4 w-4" />Marcar todas
          </button>
        ) : null}
        pestanas={<>
          <PildoraBandeja activa={tab === "unread"} onClick={() => setTab("unread")}>Sin leer{unreadCount > 0 ? ` · ${unreadCount}` : ""}</PildoraBandeja>
          <PildoraBandeja activa={tab === "all"} onClick={() => setTab("all")}>Todas</PildoraBandeja>
        </>}
      >
        {loading && pedidos.length === 0 && <EsqueletoBandeja />}
        {!loading && pedidos.length === 0 && (
          <VacioBandeja icono="inbox"
            titulo={tab === "unread" ? "Estás al día" : "Sin pedidos todavía"}
            detalle="Cuando un cliente cargue un pedido nuevo aparece acá, con aviso en el celular." />
        )}
        {grupos.map(g => (
          <div key={g.t}>
            <TituloGrupo>{g.t}</TituloGrupo>
            {g.items.map(p => (
              <FilaBandeja key={p.id} icono="inbox" tono="c"
                titulo={p.titulo || "Nuevo pedido"} vista={p.mensaje} hora={haceCuanto(p.created_at)} sinLeer={!p.leida}
                onAbrir={() => handleClickPedido(p)}
                onLeida={() => {
                  api.markNotifRead(p.id).catch(() => {});
                  setPedidos(prev => tab === "unread" ? prev.filter(n => n.id !== p.id) : prev.map(n => n.id === p.id ? { ...n, leida: true } : n));
                  setUnreadCount(c => Math.max(0, c - 1));
                }} />
            ))}
          </div>
        ))}
      </BandejaPanel>
    </div>
  );
}

